import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import {
  useSavePointOfInterestScreenshotMutation,
  ScreenshotTypeEnum,
  FileAttributes,
  useUpdatePointOfInterestFileMutation,
  FileUploadStatus,
  useUploadAttachmentsToPoiMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  PointOfInterestDocument,
} from '@/__generated__/graphql';
import { Alert } from '@mui/material';
import {
  getSizeFromBase64,
  uploadBase64ToUrl,
} from '../PointOfInterestTab/SelectedPoiContent/Tabs/UploadFileToUrl';
import { ScreenshotLocationType } from '@/types';
import { canvasToImage } from '@/utils/dom';
import { DEFAULT_SCREENSHOT_DARK_BACKGROUND } from '@/constants';
import { useCaptureScreenshot } from '@/hooks/useCaptureScreenshot';

export type SceneOptions = {
  showPointCloudInSpherical?: boolean;
  arePoisVisible?: boolean;
  areSphericalsVisible?: boolean;
};

type SaveScreenshotProps = {
  location?: ScreenshotLocationType;
  poiId: string | undefined;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdating?: boolean;
  refetchPoi?: boolean;
  showSnackbar?: boolean;
};

export const useSavePoiScreenshot = () => {
  const selectedSpherical = useAtom(state.selectedSpherical);
  const callbacks = useAtomValue(state.globalCallbacks);
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);

  const [uploadAttachmentsToPoi] = useUploadAttachmentsToPoiMutation();
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();

  const [savePointOfInterestScreenshot] = useSavePointOfInterestScreenshotMutation();
  const setAreSphericalsVisible = useSetAtom(state.areSphericalsVisible);
  const setShowPointCloudInSpherical = useSetAtom(state.showPointCloudInSpherical);
  const setArePoisVisible = useSetAtom(state.arePoisVisible);

  const { captureElement } = useCaptureScreenshot();

  const getViewpointScreenshot = useCallback(() => {
    const container = document.getElementById('outer-viewer');

    if (!container) {
      const abyssViewerParent = document.querySelector('canvas');

      if (!abyssViewerParent) {
        const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();
        return Promise.resolve(
          canvasToImage(htmlCanvas, { fillStyle: DEFAULT_SCREENSHOT_DARK_BACKGROUND })
        );
      }

      return captureElement(abyssViewerParent);
    }

    return captureElement(container);
  }, [callbacks, captureElement]);

  // Usage example:
  //    prevOptions = setSceneOptions(myOptions);
  //    setTimeout(()=>{
  //          handleSavePoiScreenshot();
  //          setSceneOptions(prevOptions);   }, 500);
  const setSceneOptions = useCallback(
    (options: SceneOptions): SceneOptions => {
      const prevOptions: SceneOptions = {};

      const { showPointCloudInSpherical, arePoisVisible, areSphericalsVisible } = options;

      if (showPointCloudInSpherical !== undefined) {
        setShowPointCloudInSpherical((current) => {
          prevOptions['showPointCloudInSpherical'] = current;
          return showPointCloudInSpherical;
        });
      }
      if (arePoisVisible !== undefined) {
        setArePoisVisible((current) => {
          prevOptions['arePoisVisible'] = current;
          return arePoisVisible;
        });
      }
      if (areSphericalsVisible !== undefined) {
        setAreSphericalsVisible((current) => {
          prevOptions['areSphericalsVisible'] = current;
          return areSphericalsVisible;
        });
      }
      return prevOptions;
    },
    [setShowPointCloudInSpherical, setArePoisVisible, setAreSphericalsVisible]
  );

  const handleSavePoiScreenshot = useCallback(
    async ({
      location = 'screenshots',
      poiId,
      isUpdating,
      setIsUpdating,
      refetchPoi,
      showSnackbar = false,
    }: SaveScreenshotProps) => {
      if (isUpdating) {
        return setSnackbarMessage({
          shouldShow: true,
          content: (
            <Alert severity="warning">
              Unable to take Screenshot at the moment. Other files are uploading. Please try once
              uploading is done
            </Alert>
          ),
        });
      } else {
        if (setIsUpdating) setIsUpdating(true);
        const viewpointScreenshot = await getViewpointScreenshot();
        if (!viewpointScreenshot || !poiId) {
          if (setIsUpdating) setIsUpdating(false);
          return '';
        }

        if (location === 'screenshots') {
          const type = selectedSpherical ? ScreenshotTypeEnum.Viewpoint : ScreenshotTypeEnum.Viewer;
          return savePointOfInterestScreenshot({
            variables: {
              input: {
                pointOfInterestId: poiId,
                type,
                image: viewpointScreenshot,
                lastModifiedFor: 'Save Screenshot',
              },
            },
            ...(refetchPoi ? { refetchQueries: [PointOfInterestDocument] } : {}),
            onCompleted: (response) => {
              const severity = response.savePointOfInterestScreenshot?.id ? 'success' : 'error';
              const text = response.savePointOfInterestScreenshot?.id
                ? 'The screenshot has been attached successfully'
                : 'Failed to attach the screenshot';
              setSnackbarMessage({
                shouldShow: showSnackbar,
                content: (
                  <Alert severity={severity} variant="filled">
                    {text}
                  </Alert>
                ),
              });
            },
          });
        } else if (location === 'attachments') {
          const screenshotFile: FileAttributes = {
            uuid: `file-${Date.now()}`,
            name: `viewer-screenshot-${Date.now()}.png`,
            mimeType: 'image/jpeg',
            size: getSizeFromBase64(viewpointScreenshot),
          };
          const { data } = await uploadAttachmentsToPoi({
            variables: {
              input: {
                pointOfInterestId: poiId,
                files: [screenshotFile],
                lastModifiedFor: 'Capture View',
              },
            },
            refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
            onError: (error) => {
              setSnackbarMessage({
                shouldShow: true,
                content: (
                  <Alert severity="error" variant="filled">
                    {error.message}
                  </Alert>
                ),
              });
            },
          });
          if (data?.uploadAttachmentsToPoi && data?.uploadAttachmentsToPoi?.length > 0) {
            const file = data.uploadAttachmentsToPoi[0];
            const uploadedFile = await uploadBase64ToUrl(viewpointScreenshot, file.url);
            if (uploadedFile)
              updatePoiFile({
                variables: {
                  input: {
                    pointOfInterestId: poiId,
                    fileId: file.id,
                    uploadStatus: FileUploadStatus.Success,
                  },
                },
                refetchQueries: [PointOfInterestDocument],
                onCompleted: () => {
                  setSnackbarMessage({
                    shouldShow: true,
                    content: (
                      <Alert severity="success" variant="filled">
                        Screenshot has been successfully uploaded
                      </Alert>
                    ),
                  });
                },
                onError: (error) => {
                  if (setIsUpdating) setIsUpdating(false);
                  setSnackbarMessage({
                    shouldShow: true,
                    content: (
                      <Alert severity="error" variant="filled">
                        {error.message}
                      </Alert>
                    ),
                  });
                },
              });
            if (setIsUpdating) setIsUpdating(false);
          }
        }
      }
    },
    [
      getViewpointScreenshot,
      selectedSpherical,
      setSnackbarMessage,
      savePointOfInterestScreenshot,
      uploadAttachmentsToPoi,
      updatePoiFile,
    ]
  );

  return { handleSavePoiScreenshot, setSceneOptions };
};
